import { useLoaderData } from 'react-router';
import type { loader as homeLoader } from '~/routes/_app._index';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { DiscoverHeader } from '../components/DiscoverHeader';
import { ScrollReveal } from '../components/ScrollReveal';

/**
 * The homepage's previous ArticlesSection, drawn while `revamp_2026` is off. Kept as it
 * was so the flag-off homepage does not change; delete it with the flag.
 */

/**
 * Articles (Figma `997:34537` header + `997:34560` cards, white section).
 *
 * Shared editorial header above a magazine-style grid of live articles — one
 * large feature card beside a 2×2 of smaller cards. Titles use Gestura
 * Headline Regular (`font-gestura`), the editorial serif from the design;
 * until the licensed woff2 is deployed it falls back to a serif. Reads
 * `articles` from the loader.
 */
const CATEGORIES = [
    { label: 'Residential', to: '/articles' },
    { label: 'New builds', to: '/articles' },
    { label: 'Beach House', to: '/articles' },
    { label: 'rural / estate / lifestyle', to: '/articles' },
];

interface ArticleCardProps {
    image?: string;
    title?: string;
    category?: string;
    date?: string;
    readTime?: string;
    link?: string;
    size: 'large' | 'small';
}

const ArticleCard = ({
    image,
    title,
    category,
    date,
    readTime,
    link,
    size,
}: ArticleCardProps) => {
    if (!title) return null;
    const meta = [date, readTime].filter(Boolean).join(', ');
    return (
        <RoccoLink to={link ?? '/articles'} className="flex flex-col">
            <div
                className={cn(
                    'bg-neutral-200 relative w-full overflow-hidden rounded-tile',
                    size === 'large' ? 'aspect-[855/597]' : 'aspect-[398/489]',
                )}
            >
                {image && (
                    <img
                        src={image}
                        alt={title}
                        className="absolute inset-0 size-full max-w-none object-cover"
                        loading="lazy"
                    />
                )}
            </div>
            <p className="m-0 font-gestura leading-[1.14] font-normal tracking-[-0.01em] text-[#1f1f21] capitalize max-md:pt-3 max-md:text-[0.8461rem] md:pt-[1.04vw] md:text-[1.458vw] lg:pt-[1.248rem] lg:text-[1.7496rem]">
                {title}
            </p>
            {category && (
                <p className="m-0 font-sans font-medium tracking-[0.08em] text-[#1f1f21] uppercase max-md:pt-2 max-md:text-[0.3622rem] md:pt-[0.83vw] md:text-[0.625vw] lg:pt-[0.996rem] lg:text-[0.75rem]">
                    {category}
                </p>
            )}
            {meta && (
                <p className="m-0 font-sans font-normal tracking-[0.08em] text-[#1f1f21] uppercase max-md:pt-0.5 max-md:text-[0.3622rem] md:pt-[0.21vw] md:text-[0.625vw] lg:pt-[0.252rem] lg:text-[0.75rem]">
                    {meta}
                </p>
            )}
        </RoccoLink>
    );
};

interface ArticlesSectionProps {
    /** Override the eyebrow (e.g. logged-in "Recently viewed"). */
    eyebrow?: string;
    /** Override the display heading (e.g. logged-in "Recently viewed Articles"). */
    heading?: string;
}

export const ArticlesSectionLegacy = ({
    eyebrow = '(04 – Articles)',
    heading = 'Editorial Insights + Articles',
}: ArticlesSectionProps = {}) => {
    const { articles } = useLoaderData<typeof homeLoader>();
    const items = (articles ?? []).map((a) => a.tile).slice(0, 5);
    const [feature, ...rest] = items;

    return (
        <section className="w-full overflow-hidden bg-white text-[#1f1f21]">
            <DiscoverHeader
                theme="light"
                fullWidthRule
                eyebrow={eyebrow}
                heading={heading}
                /* 106.32rem is 88.6% of the 1920 frame measured in rem, so it
                   only equals the md: rule's 88.6vw at exactly 1920. Below
                   that — a narrower window, or a reader whose text size has
                   grown the rem — it is wider than the section, which clips,
                   and the heading loses its last glyphs. The cap lets it wrap
                   instead; it is inert wherever the full width fits. */
                headingClassName="md:w-[88.6vw] lg:w-[106.32rem] max-w-full"
                categories={CATEGORIES}
                description="Get informed and make the best decisions for your project. We bring you insights and interviews from those at the forefront of architecture and construction."
                ctaLabel="View all Articles"
                ctaTo="/articles"
            />

            <ScrollReveal
                delay={120}
                className="grid grid-cols-2 max-md:gap-x-4 max-md:gap-y-6 max-md:px-6 max-md:pt-8 md:grid-cols-12 md:gap-x-[1.927vw] md:gap-y-[3vw] md:px-[2.61vw] md:pt-[8.33vw] lg:gap-x-[2.3124rem] lg:gap-y-[3.6rem] lg:px-[max(3.132rem,calc((100%_-_120rem)/2_+_3.132rem))] lg:pt-[9.996rem]"
            >
                {feature && (
                    <div className="max-md:col-span-2 md:col-span-6 md:row-span-2">
                        <ArticleCard
                            image={feature.image}
                            title={feature.title}
                            category={feature.category}
                            date={feature.date}
                            readTime={feature.readTime}
                            link={feature.link}
                            size="large"
                        />
                    </div>
                )}
                {rest.map((article, index) => (
                    <div key={article.ID ?? index} className="md:col-span-3">
                        <ArticleCard
                            image={article.image}
                            title={article.title}
                            category={article.category}
                            date={article.date}
                            readTime={article.readTime}
                            link={article.link}
                            size="small"
                        />
                    </div>
                ))}
            </ScrollReveal>
        </section>
    );
};
